import { motion } from 'motion/react';
import { useState } from 'react';
import { useT } from '@/shared/lib/i18n';
import { Button } from '@/shared/ui';

export type ClochardGuessProps = {
  playerName: string;
  onGuess: (guess: string) => void;
};

/**
 * Clochard's last gasp. Noir spotlight bg with the eliminated player's
 * name, a "?" mascot in gold, an ALLCAPS guess input, and a crowd
 * silhouette baked into the bottom edge. If their guess matches the
 * Flics' word they steal the win.
 */
export const ClochardGuess = ({ playerName, onGuess }: ClochardGuessProps) => {
  const t = useT();
  const [guess, setGuess] = useState('');
  const trimmed = guess.trim();

  return (
    <main
      className="relative mx-auto flex h-full w-full max-w-md flex-col items-center justify-between overflow-hidden px-7 pt-8 pb-7 text-cream"
      style={{
        background:
          'radial-gradient(circle at 50% 35%, rgba(255,210,63,.35), transparent 55%), var(--color-ink)',
      }}
    >
      <header className="text-center">
        <div className="eyebrow text-pop tracking-[0.2em]">
          {t('play.clochard.spotlight').toUpperCase()}
        </div>
        <div className="mt-1.5 font-display font-extrabold text-[28px] text-cream sm:text-[36px]">
          {t('play.clochard.last_words', { name: playerName })}
        </div>
      </header>

      <div className="flex flex-col items-center gap-4">
        <motion.div
          animate={{ scale: [1, 1.04, 1] }}
          transition={{ duration: 2.4, repeat: Number.POSITIVE_INFINITY, ease: 'easeInOut' }}
          className="flex h-[120px] w-[120px] items-center justify-center rounded-full border-[3px] border-ink font-display font-extrabold text-[56px] text-ink"
          style={{
            background: 'var(--color-white-gold)',
            filter: 'drop-shadow(0 0 30px rgba(255,210,63,.55))',
          }}
        >
          ?
        </motion.div>
        <p className="max-w-[280px] text-center text-[13px] text-cream/70 leading-[1.45]">
          You were voted out — but if you guess the Flics' word, you steal the win.
        </p>
        <div className="w-full max-w-[320px]">
          <div className="eyebrow mb-2 text-center text-pop tracking-[0.2em]">
            {t('play.clochard.one_guess').toUpperCase()}
          </div>
          <input
            value={guess}
            onChange={(e) => setGuess(e.target.value)}
            placeholder={t('play.clochard.placeholder')}
            autoFocus
            className="w-full rounded-2xl border-2 px-4 py-4 text-center font-display font-extrabold text-[26px] text-cream uppercase tracking-[0.04em] outline-none transition-colors"
            style={{
              background: 'rgba(251,247,238,0.08)',
              borderColor: 'rgba(255,210,63,0.4)',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--color-pop)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'rgba(255,210,63,0.4)';
            }}
          />
        </div>
      </div>

      <Button variant="orange" onClick={() => onGuess(trimmed)} disabled={trimmed.length === 0}>
        {t('play.clochard.submit')}
      </Button>

      {/* crowd silhouette */}
      <svg
        viewBox="0 0 400 60"
        preserveAspectRatio="none"
        role="img"
        aria-hidden
        className="pointer-events-none absolute right-0 bottom-0 left-0 h-[60px] w-full"
        style={{
          background: 'linear-gradient(to top, rgba(0,0,0,.6), transparent)',
        }}
      >
        <title>crowd silhouette</title>
        <path
          d="M0 60 L0 50 Q15 35 30 45 Q45 30 60 45 Q75 28 95 42 Q115 30 140 44 Q165 28 190 42 Q215 32 240 44 Q265 28 290 44 Q315 32 340 44 Q365 28 400 42 L400 60 Z"
          fill="rgba(0,0,0,.7)"
        />
      </svg>
    </main>
  );
};
